import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { holidayService } from '../../services/holidayService';
import { HOLIDAYS_QUERY_KEY } from './useHolidaysQuery';
import type { HolidayCreate, HolidayUpdate } from '../../models/holiday.types';

export function useHolidayMutations() {
    const queryClient = useQueryClient();
    const toast = useToast();

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: [HOLIDAYS_QUERY_KEY] });

    const reportError = (error: Error, title: string) => {
        console.error(title, error);
        toast.error(title, error.message || 'Verifique sua conexão e tente novamente.');
    };

    const createHoliday = useMutation({
        retry: false,
        mutationFn: (data: HolidayCreate) => holidayService.create(data),
        onSuccess: () => {
            invalidate();
            toast.success('Feriado adicionado', 'A data já bloqueia o calendário de férias.');
        },
        onError: (error: Error) => reportError(error, 'Não foi possível adicionar o feriado'),
    });

    const updateHoliday = useMutation({
        retry: false,
        mutationFn: ({ id, data }: { id: string; data: HolidayUpdate }) =>
            holidayService.update(id, data),
        onSuccess: () => {
            invalidate();
            toast.success('Feriado atualizado');
        },
        onError: (error: Error) => reportError(error, 'Não foi possível atualizar o feriado'),
    });

    const removeHoliday = useMutation({
        retry: false,
        mutationFn: (id: string) => holidayService.remove(id),
        onSuccess: () => {
            invalidate();
            toast.success('Feriado removido', 'A data deixa de bloquear o calendário.');
        },
        onError: (error: Error) => reportError(error, 'Não foi possível remover o feriado'),
    });

    const duplicateHolidays = useMutation({
        retry: false,
        mutationFn: ({ sourceYear, targetYear }: { sourceYear: number; targetYear: number }) =>
            holidayService.duplicateToYear(sourceYear, targetYear),
        onSuccess: (created, variables) => {
            invalidate();
            if (created === 0) {
                toast.success(
                    'Nada a duplicar',
                    `Não há feriado cadastrado em ${variables.sourceYear} que ainda falte em ${variables.targetYear}.`
                );
                return;
            }
            toast.success(
                `${created} feriados duplicados para ${variables.targetYear}`,
                'As datas foram copiadas do ano anterior — confira e corrija os móveis (Carnaval, Sexta-feira Santa, Corpus Christi).'
            );
        },
        onError: (error: Error) => reportError(error, 'Não foi possível duplicar os feriados'),
    });

    return { createHoliday, updateHoliday, removeHoliday, duplicateHolidays };
}
